import { useLayoutEffect, useMemo, useRef } from 'react';
import { useStore } from '@/context/StoreContext';
import { formatUsd } from '@/lib/format/money';
import { EXTERNAL_PATHS } from '@/lib/router';
import { shipCountryOptions } from '@/lib/store';

/**
 * The slide-in cart (bundle `gs()`, ui-layer.md §2.7 and §5.5) with the overlay additions folded
 * in: the neon checkout button and the "View full cart" link. UI-R8: while closed the drawer is
 * inert and hidden from assistive technology. Checkout opens in the same tab (StoreContext): the
 * site's own till on Stripe, shipped to the country chosen here (ΩΞ-TILL-MERCH-001).
 */
export function CartDrawer() {
  const {
    cart,
    isCartOpen,
    setCartOpen,
    busy,
    updateCartItem,
    removeFromCart,
    openCheckout,
    shipCountry,
    setShipCountry,
    checkoutError,
  } = useStore();
  const drawer = useRef<HTMLElement>(null);
  const lines = cart?.lines ?? [];
  const countries = useMemo(() => shipCountryOptions(), []);

  useLayoutEffect(() => {
    if (drawer.current !== null) drawer.current.inert = !isCartOpen;
  }, [isCartOpen]);

  const close = () => {
    setCartOpen(false);
  };

  return (
    <>
      <div className={'cart-backdrop ' + (isCartOpen ? 'cart-backdrop--open' : '')} onClick={close} />
      <aside
        ref={drawer}
        className={'cart-drawer ' + (isCartOpen ? 'cart-drawer--open' : '')}
        aria-hidden={isCartOpen ? undefined : true}
      >
        <div className="cart-drawer__header">
          <h2 className="cart-drawer__title">Your Cart</h2>
          <button className="cart-drawer__close" onClick={close} aria-label="Close cart">
            ✕
          </button>
        </div>
        {lines.length === 0 ? (
          <div className="cart-drawer__empty">
            {/* UI-R12: a decorative glyph is not a heading. As an <h2> it appeared in every
                screen reader's heading list — on every page, since the drawer is always mounted —
                announced as "Ω", and it put an h2 ahead of the page's own h1.
                `aria-hidden` removes it from the accessibility tree, which is the whole fix, and
                it is used INSTEAD of changing the tag: swapping <h2> for <p> dropped the reset's
                h2 font-size and shrank the glyph from 38×59 to 12×20. The parity rig caught that.
                An accessibility repair must not change what anyone sees. */}
            <h2 className="hero__ohm-text" aria-hidden="true">
              <span className="ohm-glyph">Ω</span>
            </h2>
            <p>Your cart is empty</p>
          </div>
        ) : (
          <>
            <div className="cart-drawer__items">
              {lines.map((line) => (
                <div key={line.id} className="cart-item">
                  {line.image ? (
                    <img src={line.image} alt={line.title} className="cart-item__image" />
                  ) : (
                    <div className="cart-item__image" />
                  )}
                  <div className="cart-item__info">
                    <span className="cart-item__title">{line.title}</span>
                    {line.variantTitle !== 'Default Title' && (
                      <span className="cart-item__variant">{line.variantTitle}</span>
                    )}
                    <div className="cart-item__qty">
                      <button
                        className="cart-item__qty-btn"
                        onClick={() => {
                          void updateCartItem(line.id, Math.max(0, line.quantity - 1));
                        }}
                        disabled={busy}
                      >
                        −
                      </button>
                      <span>{line.quantity}</span>
                      <button
                        className="cart-item__qty-btn"
                        onClick={() => {
                          void updateCartItem(line.id, line.quantity + 1);
                        }}
                        disabled={busy}
                      >
                        +
                      </button>
                    </div>
                    <span className="cart-item__price">{formatUsd(line.price * line.quantity)}</span>
                    <button
                      className="cart-item__remove"
                      onClick={() => {
                        void removeFromCart(line.id);
                      }}
                      disabled={busy}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="cart-drawer__footer">
              <div className="cart-drawer__subtotal">
                <span>Subtotal ({cart?.totalQuantity ?? 0} items)</span>
                <span>{formatUsd(cart?.subtotal ?? 0)}</span>
              </div>
              {/* ΩΞ-TILL-MERCH-001: shipping is priced by the maker for the country it ships to. */}
              <div className="cart-drawer__ship">
                <label className="cart-drawer__ship-label" htmlFor="cart-ship-country">
                  Ship to
                </label>
                <select
                  id="cart-ship-country"
                  className="cart-drawer__ship-select"
                  value={shipCountry}
                  onChange={(event) => {
                    setShipCountry(event.target.value);
                  }}
                  disabled={busy}
                >
                  {countries.map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>
              <button className="cart-drawer__checkout omega-cta-go" onClick={openCheckout} disabled={busy}>
                {busy ? 'OPENING CHECKOUT…' : 'PROCEED TO CHECKOUT'}
              </button>
              {checkoutError !== null && (
                <p className="cart-drawer__error" role="alert">
                  {checkoutError}
                </p>
              )}
              <a className="omega-cart-link" href={EXTERNAL_PATHS.cart}>
                View full cart
              </a>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
